import { useState, useEffect, useRef } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { X, XCircle, Clock, MapPin, Phone, Calendar, CheckCircle, Check, Download, Video, ChevronRight, ChevronDown, User, RefreshCw, QrCode, FileText, AlertTriangle } from 'lucide-react'
import { useUserStore, useProfileStore, useCaseStore } from '../../stores'
import { appointmentsAPI, doctorsAPI } from '../../services/api'
import { useI18n } from '../../i18n'
import { formatProviderSpecialty } from '../../utils/specialtyResolver'
import { openAppointmentIcs, getGoogleCalendarUrl, getOutlookCalendarUrl } from '../../utils/calendar'
import { formatAppointmentDate, formatAppointmentTime, formatAppointmentWeekdayDate, wallTimeToInstant } from '../../utils/dateUtils'
import { formatAddress } from '../../utils/formatAddress'
import LanguageSelector from '../../components/LanguageSelector'
import HamburgerMenu from '../../components/HamburgerMenu'
import OrellaLoader from '../../components/OrellaLoader'
import OrellaPageHeader from '../../components/OrellaPageHeader'
import AppHeader from '../../components/AppHeader'
import CheckInModal from '../../components/CheckInModal'
import { PENDING_RESCHEDULE } from '../../utils/pendingReschedule' // PROD-1007
import { isOutsideAppointment } from '../../utils/outsideAppointment' // PROD-1160
import JoinVideoVisit from '../../components/JoinVideoVisit'
import { isJoinableVisitStatus, isVirtualVisitType, joinVideoVisitLabelKey, videoJoinTarget } from '../../utils/videoVisit'
import WalletPassModal from '../../components/WalletPassModal'
import { useAgent } from '../../components/AgentContext'
import { getCardColor, normalizeCategory, ENCOUNTER_CATEGORIES, ZOOM_BLUE } from '../../config/encounterConfig'
import { visitBucket } from '../../utils/visitTiming'
import { isWithinCheckInWindow } from '../../components/casehome/appointmentWindow'
import { withoutDeclinedRows, rowCancelledByPhysician, CANCELLED } from '../../utils/declinedRequest' // PROD-983, PROD-1137
import { clinicianName, withCaseClinician } from '../../utils/clinicianName.js'


// Visits will come from API - populated by MD panel

// Resolve a visit's doctor name. Appointment fields first; then fall back to
// the linked case's claiming doctor — share-code cases claimed by an Orella
// doctor have no appointment doctor_name, but the case carries the doctor
// (sentToDoctorName, or a doctor object with first/last name).
function resolveDoctorName(visit, linkedCase) {
  return visit?.doctor_name || visit?.doctorName
    || linkedCase?.sentToDoctorName
    || (linkedCase?.doctor ? clinicianName(withCaseClinician(linkedCase.doctor, linkedCase)) : '')
    || 'Doctor'
}

export default function VisitsScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (location.state?.openMenu) { setMenuOpen(true); window.history.replaceState({}, '') }
  }, [location.state])
  const user = useUserStore((state) => state.user)
  const profile = useProfileStore((state) => state.profile)
  const cases = useCaseStore((state) => state.cases)
  const { t, hasText, currentLanguage } = useI18n()
  const { openAgent } = useAgent()
  const [menuOpen, setMenuOpen] = useState(false)
  const [showLanguageModal, setShowLanguageModal] = useState(false)
  const [showAddVisitModal, setShowAddVisitModal] = useState(false)
  const [expandedVisitId, setExpandedVisitId] = useState(location.state?.appointmentId || null)
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCards, setShowCards] = useState(false)
  const [timeFilter, setTimeFilter] = useState('30d') // 14d | 30d | 6mo | 12mo | all
  const [showTimeDropdown, setShowTimeDropdown] = useState(false)
  const [visitStatusFilter, setVisitStatusFilter] = useState('all') // all | upcoming | past | cancelled

  // v188.5: Check-in modal state for opening pass directly from visits
  const [checkInVisitId, setCheckInVisitId] = useState(null) // visit.id of the active check-in modal
  // s126c: the modal was opened from a Join tap (here, or ?join=1 from Diagnose / Deliver): it joins by itself
  const [checkInJoin, setCheckInJoin] = useState(false)
  const openJoinCheckIn = async (visit) => {
    const caseId = visit?.case_id || visit?.caseId || ''
    try {
      const store = useCaseStore.getState()
      let c = store.cases?.find(x => x.id === caseId)
      if (!c && caseId) { await store.loadCases(); c = useCaseStore.getState().cases?.find(x => x.id === caseId) }
      if (c) store.setCurrentCase(c)
    } catch (err) { console.error('[Visits] join check-in error:', err?.name) }
    setCheckInJoin(true)
    setCheckInVisitId(visit.id)
  }
  // v223.9.3: Wallet-style pass popup state (the designed card)
  const [passData, setPassData] = useState(null)

  // v226.9.3 (G3): Deep-link actions from notifications.
  //   ?openCheckIn=1&caseId=... -> launch the front-desk check-in modal for that case
  //   ?openPass=1&caseId=...    -> open the scannable wallet QR pass for that case
  // Waits for visits to load, acts once, then strips the params so refresh won't re-fire.
  useEffect(() => {
    const wantCheckIn = searchParams.get('openCheckIn') === '1'
    const wantPass = searchParams.get('openPass') === '1'
    if (!wantCheckIn && !wantPass) return
    if (loading) return

    const targetCaseId = searchParams.get('caseId') || ''
    // s126c: a Join from Diagnose / Deliver names its visit (visitId) and asks the modal to join (join=1)
    const wantJoin = wantCheckIn && searchParams.get('join') === '1'
    const namedVisitId = searchParams.get('visitId') || ''
    const forCase = (a) => (a.case_id || a.caseId) === targetCaseId
    // ★ PROD-1007: a held reschedule (pending_reschedule) is a request, not a visit to check in to;
    //   the check-in deep link never picks it (the old confirmed row, if still booked, is picked).
    // ★ PROD-1137: nor a cancelled visit (it was candidates[0] when the case had no other row).
    // ★ PROD-1160 (review L3): nor the patient's outside visit (source 'manual_external'): it has no
    //   Orella check-in or pass, and it was candidates[0] when the case had no confirmed Orella row.
    const candidates = appointments.filter(a => !isOutsideAppointment(a) && (!targetCaseId || forCase(a)) && !(wantCheckIn && a.status === PENDING_RESCHEDULE) && !(wantCheckIn && a.status === CANCELLED))
    const visit = (namedVisitId && candidates.find(a => a.id === namedVisitId)) || candidates.find(a => (a.status || 'confirmed') === 'confirmed') || candidates[0]

    const clear = () => {
      const next = new URLSearchParams(searchParams)
      next.delete('openCheckIn'); next.delete('openPass'); next.delete('caseId'); next.delete('join'); next.delete('visitId')
      setSearchParams(next, { replace: true })
    }
    if (!visit) { clear(); return }
    const caseId = visit.case_id || visit.caseId || ''

    if (wantPass && caseId && isCaseCheckedIn(caseId)) {
      ;(async () => {
        try {
          const store = useCaseStore.getState()
          let c = store.cases?.find(x => x.id === caseId)
          if (!c) { await store.loadCases(); c = useCaseStore.getState().cases?.find(x => x.id === caseId) }
          if (c) store.setCurrentCase(c)
          const ident = profile?.identification || {}
          const dt = formatDate(visit.date)
          const pCity = visit.practice_city || visit.city || ''
          const pState = visit.practice_state || ''
          const pZip = visit.practice_zip || ''
          setPassData({
            caseId: c?.id || caseId,
            token: c?.checkIn?.token || null,
            patientName: `${ident.firstName || ''} ${ident.lastName || ''}`.trim() || 'Patient',
            providerName: resolveDoctorName(visit, c),
            practiceName: visit.practice_name || visit.practiceName || '',
            addressLine1: formatAddress(visit.practice_address || visit.address || ''),
            addressLine2: `${pCity}${pState ? ', ' + pState : ''}${pZip ? ' ' + pZip : ''}`.replace(/^,\s*/, '').trim(),
            dateLabel: dt?.full || '',
            timeLabel: formatTimeDisplay(visit.time),
          })
        } catch (err) { console.error('[Visits] openPass deep-link error:', err) }
      })()
    } else {
      // openCheckIn, or openPass before check-in (no token yet) -> launch check-in.
      ;(async () => {
        try {
          const store = useCaseStore.getState()
          let c = store.cases?.find(x => x.id === caseId)
          if (!c && caseId) { await store.loadCases(); c = useCaseStore.getState().cases?.find(x => x.id === caseId) }
          if (c) store.setCurrentCase(c)
        } catch (err) { console.error('[Visits] openCheckIn deep-link error:', err) }
        setExpandedVisitId(visit.id)
        setCheckInJoin(wantJoin)
        setCheckInVisitId(visit.id)
      })()
    }
    clear()
  }, [searchParams, loading, appointments])

  // v223.9.3: Refresh cases from the server when Visits mounts. Check-in state
  // (status + the check-in token) lives on the server; without a fresh load a
  // second device can render a stale "not checked in" view for a case that was
  // checked in elsewhere. This makes the pass/check-in state consistent across
  // devices.
  useEffect(() => {
    try { useCaseStore.getState().loadCases?.() } catch { /* non-fatal */ }
  }, [])
  
  // v200.2: Cancel appointment confirmation state
  const [showCancelConfirm, setShowCancelConfirm] = useState(null)
  // PROD-1160 (review L-C): Remove of an outside visit is busy while its DELETE runs (one tap, one request).
  const [removingId, setRemovingId] = useState(null)
  const removingRef = useRef(false)
  
  // Helper to get case status from store
  const getCaseStatus = (caseId) => {
    if (!caseId) return null
    const foundCase = cases.find(c => c.id === caseId)
    return foundCase?.status || null
  }
  
  // Helper to check if a case is checked in.
  // v223.9.3: A case is "checked in" if the server-persisted check-in token is
  // present (case.checkIn.token) OR status === 'checked_in'. Keying on the token
  // — which is written server-side at check-in and surfaced by loadCases — makes
  // the state consistent across devices, where a status-only check could be
  // skewed by the client-side status merge.
  const isCaseCheckedIn = (caseId) => {
    if (!caseId) return false
    const foundCase = cases.find(c => c.id === caseId)
    if (!foundCase) return false
    return !!(foundCase.checkIn && foundCase.checkIn.token) || foundCase.status === 'checked_in'
  }

  // Fetch appointments on mount
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const result = await appointmentsAPI.getAll()
        // ★ PROD-983: GET /appointments also returns rows the physician declined ('rejected'). Such a
        //   row is no visit: it was listed as upcoming (Join for video, Reschedule, and the check-in
        //   deep link's fallback candidate). Case Home shows the declined request instead.
        let appts = withoutDeclinedRows(result || [])
        
        // Enrich appointments with doctor info
        // v200.2: Always look up doctors to populate doctor_specialty for the three-field header
        const hasAnyDoctor = appts.some(a => a.doctor_id || a.doctorId)
        
        if (hasAnyDoctor) {
          try {
            const allDoctors = await doctorsAPI.list()
            appts = appts.map(a => {
              const docId = a.doctor_id || a.doctorId
              if (!docId) return a
              const doc = allDoctors.find(d => d.id === docId)
              if (!doc) return a
              
              // Always set doctor_specialty from doctor record
              const enriched = {
                ...a,
                doctor_specialty: doc.specialty || '',
              }
              
              // Also backfill practice data if missing
              if (!a.practice_address && !a.practice_phone) {
                enriched.practice_name = a.practice_name || doc.practice_name || ''
                enriched.practice_address = doc.practice_address || ''
                enriched.practice_city = doc.practice_city || ''
                enriched.practice_state = doc.practice_state || ''
                enriched.practice_zip = doc.practice_zip || ''
                enriched.practice_phone = doc.practice_phone || doc.phone || ''
                enriched.specialty = a.specialty || doc.specialty || ''
              }
              
              return enriched
            })
          } catch (enrichErr) {
            console.warn('[Visits] Doctor enrichment failed:', enrichErr)
          }
        }
        
        setAppointments(appts)
      } catch (err) {
        console.error('Error fetching appointments:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchAppointments()

    // v163.6.0: Refresh when user returns to tab/app
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchAppointments()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [])
  // v201.9: Scroll to top on mount
  // v218.0.4: The mount-only reset raced the async appointment fetch — it fired
  // while the page was still short (loader showing), then content grew tall and
  // nothing re-scrolled, leaving My Visits opened scrolled down. Re-assert the
  // reset when `loading` flips false (real content has rendered), plus a short
  // delayed pass for any post-render layout settle (v177.3.5 pattern).
  useEffect(() => {
    const resetScroll = () => {
      window.scrollTo({ top: 0, behavior: 'instant' })
      const sw = document.querySelector('.scroll-wrapper')
      if (sw) sw.scrollTop = 0
    }
    resetScroll()
    const t = setTimeout(resetScroll, 100)
    return () => clearTimeout(t)
  }, [loading])


  // Helpers


  // Split appointments into upcoming / past / cancelled (s101: utils/visitTiming.js)
  const now = new Date()
  // v212.3: Time filter for visits
  const getVisitTimeFilterDate = () => {
    const d = new Date()
    if (timeFilter === '14d') return new Date(d.getTime() - 14 * 24 * 60 * 60 * 1000)
    if (timeFilter === '30d') return new Date(d.getTime() - 30 * 24 * 60 * 60 * 1000)
    if (timeFilter === '6mo') { d.setMonth(d.getMonth() - 6); return d }
    if (timeFilter === '12mo') { d.setFullYear(d.getFullYear() - 1); return d }
    return null // 'all'
  }
  const visitTimeFilterDate = getVisitTimeFilterDate()

  const timeFilteredAppointments = visitTimeFilterDate
    ? appointments.filter(a => {
        const apptDate = new Date(a.date || a.created_at)
        return apptDate >= visitTimeFilterDate
      })
    : appointments

  // ★★★ s101 (Dr. Rami, 2026-10-12): ONE answer to "is this visit past?" — utils/visitTiming.js.
  //   Finalized (the API completes the row) OR start + duration + 1 hour has passed. The
  //   v193.13 "same-day is always upcoming" rule is retired: it kept a finished, finalized
  //   1:00 PM visit under Upcoming all afternoon (Build 65, case 8AC3FCD4).
  const upcomingVisits = timeFilteredAppointments.filter(a => visitBucket(a, now) === 'upcoming')
  const pastVisits = timeFilteredAppointments.filter(a => visitBucket(a, now) === 'past')
  const cancelledVisits = timeFilteredAppointments.filter(a => visitBucket(a, now) === 'cancelled')

  // v212.3: Apply status filter
  // ★★★ v242.132.0 — THESE WERE ENGLISH LITERALS IN JSX, ON A SCREEN THAT
  //     TRANSLATES. Reported from TestFlight as "untranslated keys in Spanish":
  //     the section headings read PRÓXIMAS / COMPLETADAS while the filter chips
  //     beside them still said "30 Days", "All", "Upcoming", and the cards said
  //     "Checked In" and "Open Pass". check-i18n-keys cannot see this class —
  //     it verifies that every t() KEY resolves, and a hardcoded string never
  //     calls t() at all, so it is invisible to the one gate that looks at
  //     translations. Declared once here rather than twice inline, because the
  //     chip label and the dropdown list were two copies of the same five
  //     strings and only one of them would ever have been found.
  const TIME_FILTERS = [
    { key: '14d', label: t('visits.filterTwoWeeks') },
    { key: '30d', label: t('visits.filter30Days') },
    { key: '6mo', label: t('visits.filter6Months') },
    { key: '12mo', label: t('visits.filter12Months') },
    { key: 'all', label: t('visits.filterAllTime') },
  ]

  const showUpcoming = visitStatusFilter === 'all' || visitStatusFilter === 'upcoming'
  const showPast = visitStatusFilter === 'all' || visitStatusFilter === 'past'
  // PROD-1137: the Cancelled chip counted these rows but no section drew them; they show under All too.
  const showCancelled = visitStatusFilter === 'all' || visitStatusFilter === 'cancelled'

  const formatDate = (dateString) => formatAppointmentDate(dateString)
  
  // Helper to format time for display (24-hour or AM/PM -> friendly format)
  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return ''
    // If already in AM/PM format, return as-is
    if (/AM|PM/i.test(timeStr)) return timeStr
    return formatAppointmentTime(timeStr)
  }

  // Helper to get case name from local cases store by ID
  const getCaseNameById = (caseId) => {
    if (!caseId) return null
    const foundCase = cases.find(c => c.id === caseId)
    if (foundCase) {
      // Try to get focus area name
      const focusAreaName = foundCase.focusArea?.name || foundCase.focus_area
      if (focusAreaName) return focusAreaName
      // Fall back to specialty name
      const specialtyName = foundCase.specialty?.name || foundCase.specialty
      if (specialtyName) return specialtyName
    }
    return null
  }

  // v200.2: Get full case object for enriched card display
  const getCaseById = (caseId) => {
    if (!caseId) return null
    return cases.find(c => c.id === caseId) || null
  }

  const handleGetDirections = (fullAddress) => {
    if (!fullAddress) {
      alert(t('visits.noAddress'))
      return
    }
    const query = encodeURIComponent(fullAddress)
    window.open(`https://maps.google.com/maps?q=${query}`, '_blank')
  }

  const handleAddToCalendar = (visit, doctorName, fullAddress, calendarType = 'google') => {
    const appointment = {
      title: `${doctorName} - ${visit.specialty || t('visits.medicalAppointment')}`,
      description: t('visits.appointmentFor', { focusArea: visit.focus_area || 'consultation' }) + (visit.practice_name ? '\n' + visit.practice_name : ''),
      date: visit.date,
      time: visit.time || '09:00',
      duration: visit.duration || 30,
      location: fullAddress || '',
      organizer: doctorName || 'Orella Health',
    }
    
    if (calendarType === 'ics') {
      // ★ v242.106.0 — the .ics is a URL now, keyed on the APPOINTMENT id, and
      //   iOS presents its own Add-Event sheet instead of the share sheet
      //   (TF b39 #6). Still unawaited-with-a-catch: v242.87.0's lesson stands —
      //   an unhandled rejection makes this menu item look inert, which is
      //   exactly how the previous defect was reported.
      openAppointmentIcs(visit.id, currentLanguage)
        .catch(err => console.error('[Visits] .ics open failed:', err))
    } else if (calendarType === 'outlook') {
      window.open(getOutlookCalendarUrl(appointment), '_blank')
    } else {
      window.open(getGoogleCalendarUrl(appointment), '_blank')
    }
  }

  // PROD-1137: a cancelled visit is a visit to show (a first visit the physician cancelled is often the only one).
  const hasNoVisits = upcomingVisits.length === 0 && pastVisits.length === 0 && cancelledVisits.length === 0
  
  // Calendar dropdown state — v217.1.3: now carries the trigger's bounding
  // rect so the menu can render via portal at fixed coords (escapes the
  // card's overflow-hidden clipping when collapsed).
  const [showCalendarMenu, setShowCalendarMenu] = useState(null) // { id, rect } | null

  // v217.1.3: When the menu is open and the user scrolls or resizes the
  // viewport, the trigger moves but the portal-positioned menu doesn't —
  // close the menu to avoid a disconnected popover.
  useEffect(() => {
    if (!showCalendarMenu) return
    const close = () => setShowCalendarMenu(null)
    window.addEventListener('scroll', close, true) // capture so nested scrollers also fire
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [showCalendarMenu])

  // v143.4: Show loading spinner while fetching appointments
  if (loading) {
    return (
      <div className="min-h-full bg-white flex flex-col">
        <OrellaLoader message={t('visits.loadingVisits')} />
      </div>
    )
  }

  return (
    <div className="min-h-full bg-white flex flex-col relative">
      {/* Header — shared navy pattern (v242.17.0), carries to the top via NAVY_HEADER_ROUTES */}
      <AppHeader
        icon={Calendar}
        title={t('visits.title')}
        onMenu={() => setMenuOpen(true)}
        onLanguage={() => setShowLanguageModal(true)}
      />

      {/* Language Selector Modal */}
      <LanguageSelector 
        isOpen={showLanguageModal} 
        onClose={() => setShowLanguageModal(false)}
      />

      {/* Orella Dialogue — below heading */}
      <OrellaPageHeader
        message={t('visits.visitsMessage')}
        sessionKey="visits"
        chips={[
          { label: t('visits.askOrella'), onClick: () => openAgent() },
        ]}
        onAnimationDone={() => setShowCards(true)}
      />

      {/* Content — gated behind Orella animation */}
      <div className={`flex-1 px-4 pb-24 transition-all duration-500 ${showCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
        {/* v212.5: Time dropdown + Status filter pills */}
        <div className="mb-3">
          <div className="flex gap-1.5 items-center flex-wrap">
            {/* Time dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowTimeDropdown(!showTimeDropdown)}
                className="px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap flex items-center gap-1 transition-colors"
                style={{ backgroundColor: '#46A8F9', color: 'white' }}
              >
                <Calendar size={11} />
                {TIME_FILTERS.find(f => f.key === timeFilter)?.label}
                <ChevronDown size={11} />
              </button>
              {showTimeDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowTimeDropdown(false)} />
                  <div className="absolute top-[calc(100%+4px)] left-0 z-20 bg-white rounded-xl py-1 border border-slate-200 min-w-[130px]"
                    style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  >
                    {TIME_FILTERS.map(f => (
                      <button
                        key={f.key}
                        onClick={() => { setTimeFilter(f.key); setShowTimeDropdown(false) }}
                        className={`block w-full text-left px-3.5 py-2 text-xs ${
                          timeFilter === f.key ? 'font-semibold text-orella-500 bg-orella-50' : 'text-slate-600'
                        }`}
                      >{f.label}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* Status pills */}
            {[
              { key: 'all', label: t('visits.filterAll'), count: upcomingVisits.length + pastVisits.length + cancelledVisits.length },
              { key: 'upcoming', label: t('visits.filterUpcoming'), count: upcomingVisits.length },
              { key: 'past', label: t('visits.filterPast'), count: pastVisits.length },
              { key: 'cancelled', label: t('visits.filterCancelled'), count: cancelledVisits.length },
            // s101 (Alex, Build 65: "Can I also see past appointments?"): Past is ALWAYS offered —
            //   hiding an empty chip hid the only way in. Cancelled still appears only when it has rows.
            ].filter(f => f.key !== 'cancelled' || f.count > 0).map(f => (
              <button
                key={f.key}
                onClick={() => setVisitStatusFilter(f.key)}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap transition-colors flex items-center gap-1.5"
                style={visitStatusFilter === f.key 
                  ? { backgroundColor: '#15354F', color: 'white' } 
                  : { border: '1px solid #E2E8F0', color: '#64748B' }
                }
              >
                {f.label}
                {f.count > 0 && visitStatusFilter !== f.key && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#F1F5F9', color: '#94A3B8' }}>{f.count}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {hasNoVisits ? (
          /* Empty State */
          <div className="mt-8 bg-gray-50 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#E9F8FF' }}>
              <Calendar className="text-[#46A8F9]" size={32} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: '#15354F' }}>{t('visits.noVisits')}</h3>
            <p className="text-gray-500 text-sm mb-6">
              {t('visits.noVisitsHint')}
            </p>
            <button
              onClick={() => navigate('/home')}
              className="px-6 py-3 border-2 border-[#46A8F9] text-[#46A8F9] rounded-full font-medium"
            >
              {t('visits.startNewCase')}
            </button>
          </div>
        ) : (
          <>
            {/* Upcoming Section */}
            {showUpcoming && (
            <div className="mb-6">
              <p className="text-[11px] font-bold uppercase tracking-wider mb-3" style={{ color: '#46A8F9' }}>{t('visits.upcoming')}</p>
              {upcomingVisits.length === 0 ? (
                <p className="text-gray-500 text-sm">{t('visits.noUpcoming')}</p>
              ) : (
                <div className="space-y-4">
                  {upcomingVisits.map((visit) => {
                    const date = formatDate(visit.date)
                    // Use API field names (snake_case)
                    const practiceName = visit.practice_name || visit.practiceName || ''
                    // v222.4.8 — defensive: practice_address may be a JSONB
                    // dict / JSON-string from older appointment rows or
                    // pre-v222.4.8 API responses still in flight.
                    const practiceAddress = formatAddress(visit.practice_address || visit.address || '')
                    const practiceCity = visit.practice_city || visit.city || ''
                    const practiceState = visit.practice_state || ''
                    const practiceZip = visit.practice_zip || ''
                    const practicePhone = visit.practice_phone || visit.phone || ''
                    const specialty = visit.specialty || ''
                    const focusArea = visit.focus_area || ''
                    const caseId = visit.case_id || visit.caseId || ''
                    const fullAddress = `${practiceAddress}${practiceCity ? ', ' + practiceCity : ''}${practiceState ? ', ' + practiceState : ''} ${practiceZip}`.trim()
                    
                    // v200.2: Three-field header data
                    const linkedCase = getCaseById(caseId)
                    const doctorName = resolveDoctorName(visit, linkedCase)
                    const doctorSpecialty = visit.doctor_specialty || ''
                    // Patient's selected category (from case's specialty field, e.g. "Ear")
                    const patientCategory = linkedCase?.specialtyName || linkedCase?.specialty?.name || linkedCase?.specialty || ''
                    // Focus area from appointment or case (e.g. "Muffling")
                    const caseFocusArea = focusArea || linkedCase?.focusAreaName || linkedCase?.focusArea?.name || linkedCase?.focus_area || ''
                    // Case number for display
                    const caseNumber = linkedCase?.shareCode || linkedCase?.share_code || ''
                    
                    // v118: Derive card color from encounter category
                    const aptCategory = normalizeCategory(visit.appointment_category || visit.appointmentCategory)
                    const aptStatus = visit.status || 'confirmed'
                    const cc = getCardColor(aptStatus, aptCategory)
                    const isExpanded = expandedVisitId === visit.id
                    const isExternalVisit = isOutsideAppointment(visit) // PROD-1160 (review L-A): pinned by check-outside-appointment-skip
                    
                    // Check-in eligibility: confirmed, within 24hr window, has caseId
                    // v189.0 BT-10: Also block check-in when case is completed/finalized
                    // ★ PROD-1160 (review L2): the patient's outside visit has no Orella check-in, pass,
                    //   Join or Reschedule; its Cancel removes the patient's own entry (DELETE, PROD-1140).
                    const isCheckedIn = !isExternalVisit && isCaseCheckedIn(caseId)
                    const caseStatus = getCaseStatus(caseId)
                    const caseIsClosed = ['completed', 'finalized', 'closed'].includes(caseStatus)
                    const checkInEligible = !isExternalVisit && !isCheckedIn && !caseIsClosed && caseId && aptStatus === 'confirmed' && (() => {
                      const vDate = visit.date
                      const vTime = visit.time
                      if (!vDate || !vTime) return false
                      // ★ PROD-1368: the visit time is the practice's wall clock (ORELLA_TZ), not the device's.
                      const apptInstant = wallTimeToInstant(vDate, vTime)
                      if (!apptInstant) return false
                      const hoursUntil = (apptInstant.getTime() - Date.now()) / (1000 * 60 * 60)
                      return isWithinCheckInWindow(hoursUntil) // PROD-1369: the shared boundary
                    })()
                    
                    // Card accent: Orella blue for checked-in or check-in eligible
                    // v242.101.0 — teal (#0D9488) is retired app-wide; check-in wears Orella blue.
                    const showCheckInAccent = isCheckedIn || checkInEligible

                    return (
                      <div key={visit.id} className="bg-white rounded-[14px] overflow-hidden transition-all" style={{ border: `1.5px solid ${showCheckInAccent ? '#46A8F9' : cc.border}` }}>
                        {/* Top accent bar */}
                        <div className="h-[3px]" style={{ backgroundColor: showCheckInAccent ? '#46A8F9' : cc.primary }} />
                        
                        {/* Collapsed banner — v200.2 redesign */}
                        <div className="flex items-center p-3.5 gap-3">
                          {/* Date square */}
                          <div className="w-12 h-12 rounded-[10px] flex flex-col items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: '#15354F' }}>
                            <span className="text-[9px] font-bold uppercase" style={{ color: 'rgba(255,255,255,0.6)' }}>{date.month}</span>
                            <span className="text-[20px] font-extrabold leading-none">{date.day}</span>
                          </div>

                          {/* Content area — tappable to expand */}
                          <button
                            onClick={() => setExpandedVisitId(isExpanded ? null : visit.id)}
                            className="flex-1 min-w-0 text-left active:bg-gray-50 transition-colors rounded-lg -m-1 p-1"
                          >
                            {/* v200.2: Three-field header — Doctor Specialty / Patient Category / Focus Area */}
                            {(doctorSpecialty || patientCategory || caseFocusArea) && (
                              <p className="text-[11px] font-semibold mb-0.5 flex items-center gap-0 flex-wrap">
                                {doctorSpecialty && <span style={{ color: '#46A8F9' }}>{formatProviderSpecialty(doctorSpecialty, t)}</span>}
                                {doctorSpecialty && patientCategory && <span className="text-gray-300 mx-1.5">/</span>}
                                {patientCategory && <span className="text-gray-500">{patientCategory}</span>}
                                {(doctorSpecialty || patientCategory) && caseFocusArea && <span className="text-gray-300 mx-1.5">/</span>}
                                {caseFocusArea && <span className="text-gray-400">{caseFocusArea}</span>}
                              </p>
                            )}
                            <div className="flex items-center gap-2">
                              <h3 className="text-[14px] font-bold truncate" style={{ color: '#15354F' }}>{doctorName}</h3>
                              {isExternalVisit && (
                                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: '#E9F8FF', color: '#46A8F9' }}>{t('visits.myDoctor')}</span>
                              )}
                            </div>
                            {/* v200.2: Case number */}
                            {caseNumber && (
                              <p className="text-[10.5px] font-medium text-gray-400 mt-0.5">{t('caseHome.case', 'Case')} {caseNumber}</p>
                            )}
                            <div className="flex items-center gap-2.5 mt-1 flex-wrap">
                              <span className="flex items-center gap-1 text-[11px] font-medium min-w-0" style={{ color: '#46A8F9' }}>
                                <Clock size={11} className="flex-shrink-0" /> {[formatAppointmentWeekdayDate(visit.date), formatTimeDisplay(visit.time)].filter(Boolean).join(' · ')}
                              </span>
                              <span className="flex items-center gap-1 text-[11px] text-gray-400">
                                {(visit.type === 'video' || visit.type === 'telehealth')
                                  ? <><Video size={11} /> {t('appointment.telehealth', 'Telehealth')}</>
                                  : <><MapPin size={11} /> {t('caseHome.inPerson')}</>
                                }
                              </span>
                            </div>
                            {/* Check-in / Checked-in indicator on collapsed row */}
                            {isCheckedIn && !isExpanded && (
                              <div className="flex items-center gap-1.5 mt-1.5">
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#46A8F9' }} />
                                <span className="text-[11px] font-semibold" style={{ color: '#15354F' }}>{t('visits.checkedIn')}</span>
                              </div>
                            )}
                            {checkInEligible && !isCheckedIn && !isExpanded && (
                              <div className="flex items-center gap-1.5 mt-1.5">
                                <span className="w-2 h-2 rounded-full flex-shrink-0 breathe-green" style={{ backgroundColor: '#46A8F9' }} />
                                <span className="text-[11px] font-semibold" style={{ color: '#15354F' }}>{t('visits.checkInAvailable', 'Check-in available')}</span>
                              </div>
                            )}
                          </button>

                          {/* v200.2: Calendar icon button in banner.
                              v217.1.3: dropdown was clipped by card's overflow-hidden
                              when collapsed. We now record the trigger rect on open
                              and render the menu via portal to document.body (see
                              the portal block at the bottom of the component). */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              if (showCalendarMenu?.id === visit.id) {
                                setShowCalendarMenu(null)
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect()
                                setShowCalendarMenu({ id: visit.id, rect })
                              }
                            }}
                            className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform"
                            style={{ border: '1.5px solid #A7D9FC', backgroundColor: '#E9F8FF' }}
                            title={t('visits.addToCalendar')}
                          >
                            <Calendar size={16} style={{ color: '#46A8F9' }} />
                          </button>

                          {/* Chevron */}
                          <button
                            onClick={() => setExpandedVisitId(isExpanded ? null : visit.id)}
                            className="flex-shrink-0 p-0.5"
                          >
                            <ChevronDown size={18} className="text-gray-300 transition-transform" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }} />
                          </button>
                        </div>

                        {/* v227.9.0: Open Pass surfaced on the collapsed card — a
                            checked-in patient gets the scannable pass in one tap
                            without having to expand. Renders in both states. */}
                        {isCheckedIn && caseId && (
                          <div className="px-3.5 pb-3.5">
                            <button
                              onClick={async () => {
                                try {
                                  const store = useCaseStore.getState()
                                  let existingCase = store.cases?.find(c => c.id === caseId)
                                  if (existingCase) {
                                    store.setCurrentCase(existingCase)
                                  } else {
                                    await store.loadCases()
                                    existingCase = useCaseStore.getState().cases?.find(c => c.id === caseId)
                                    if (existingCase) store.setCurrentCase(existingCase)
                                  }
                                  const token = existingCase?.checkIn?.token || null
                                  const ident = profile?.identification || {}
                                  setPassData({
                                    caseId: existingCase?.id || caseId,
                                    token,
                                    patientName: `${ident.firstName || ''} ${ident.lastName || ''}`.trim() || 'Patient',
                                    providerName: doctorName,
                                    practiceName,
                                    addressLine1: practiceAddress,
                                    addressLine2: `${practiceCity}${practiceState ? ', ' + practiceState : ''}${practiceZip ? ' ' + practiceZip : ''}`.replace(/^,\s*/, '').trim(),
                                    dateLabel: date?.full || '',
                                    timeLabel: formatTimeDisplay(visit.time),
                                  })
                                } catch (err) {
                                  console.error('[Visits] Open Pass error:', err)
                                }
                              }}
                              className="w-full py-3 rounded-lg text-[13px] font-bold flex items-center justify-center gap-2 text-white"
                              style={{ backgroundColor: '#46A8F9' }}
                            >
                              <QrCode size={16} strokeWidth={2.5} />
                              {t('visits.openPass')}
                            </button>
                          </div>
                        )}

                        {/* Expanded details */}
                        {isExpanded && (
                          <div className="px-3.5 pb-3.5" style={{ animation: 'fadeIn 0.2s ease' }}>
                            <div className="h-px bg-gray-100 mb-3" />
                            
                            {/* In-person: clickable address with map link */}
                            {!(visit.type === 'video' || visit.type === 'telehealth') && (practiceName || practiceAddress) && (
                              <a 
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-start gap-2 hover:bg-gray-50 -mx-1 px-1 py-0.5 rounded-lg transition-colors mb-2"
                              >
                                <MapPin size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#46A8F9' }} />
                                <div>
                                  {practiceName && <p className="font-semibold text-[13px] text-gray-800">{practiceName}</p>}
                                  {practiceAddress && <p className="text-[12px]" style={{ color: '#46A8F9' }}>{practiceAddress}</p>}
                                  {(practiceCity || practiceState) && (
                                    <p className="text-[12px]" style={{ color: '#46A8F9' }}>
                                      {practiceCity}{practiceState ? `, ${practiceState}` : ''} {practiceZip}
                                    </p>
                                  )}
                                </div>
                              </a>
                            )}
                            
                            {/* Telehealth: practice name + virtual indicator */}
                            {(visit.type === 'video' || visit.type === 'telehealth') && practiceName && (
                              <div className="flex items-start gap-2 mb-2">
                                <Video size={14} className="mt-0.5 flex-shrink-0" style={{ color: ZOOM_BLUE }} />
                                <div>
                                  <p className="font-semibold text-[13px] text-gray-800">{practiceName}</p>
                                  <p className="text-[12px]" style={{ color: ZOOM_BLUE }}>{t('visits.virtualVisit')}</p>
                                </div>
                              </div>
                            )}

                            {/* Phone — always shown */}
                            {practicePhone && (
                              <div className="flex items-center gap-2">
                                <Phone size={14} className="text-gray-400" />
                                <a href={`tel:${practicePhone}`} className="text-[12px] font-medium" style={{ color: '#46A8F9' }}>
                                  {practicePhone}
                                </a>
                              </div>
                            )}

                        {/* View Doctor Profile */}
                        {(visit.doctor_id || visit.doctorId) && (
                          <button
                            onClick={async () => {
                              try {
                                const docId = visit.doctor_id || visit.doctorId
                                if (docId) {
                                  navigate(`/doctor/${docId}`, { state: { mode: 'view' } })
                                } else {
                                  // No doctor ID — build a minimal doctor object and navigate with it
                                  navigate(`/doctor/unknown`, { state: { mode: 'view', doctor: {
                                    first_name: doctorName.replace(/^Dr\.?\s*/i, '').split(' ')[0] || '',
                                    last_name: doctorName.replace(/^Dr\.?\s*/i, '').split(' ').slice(1).join(' ') || '',
                                    specialty, practice_name: practiceName, practice_address: practiceAddress,
                                    practice_city: practiceCity, practice_state: practiceState, practice_zip: practiceZip,
                                    phone: practicePhone
                                  }}})
                                }
                              } catch { 
                                navigate(`/doctor/unknown`, { state: { mode: 'view', doctor: {
                                  first_name: doctorName.replace(/^Dr\.?\s*/i, '').split(' ')[0] || '',
                                  last_name: doctorName.replace(/^Dr\.?\s*/i, '').split(' ').slice(1).join(' ') || '',
                                  specialty, practice_name: practiceName, practice_address: practiceAddress,
                                  practice_city: practiceCity, practice_state: practiceState, practice_zip: practiceZip,
                                  phone: practicePhone
                                }}})
                              }
                            }}
                            className="flex items-center gap-1.5 text-[12px] font-semibold mb-3 pt-2 border-t border-gray-100 w-full"
                            style={{ color: '#46A8F9' }}
                          >
                            <User size={13} /> {t('visits.viewDoctorProfile', 'View Doctor Profile')}
                          </button>
                        )}

                        {/* v200.2: View Case — outlined pill button */}
                        {(caseId || caseFocusArea) && (
                          <button 
                            onClick={async () => {
                              if (!caseId) return
                              try {
                                const store = useCaseStore.getState()
                                const existingCase = store.cases?.find(c => c.id === caseId)
                                if (existingCase) {
                                  store.setCurrentCase(existingCase)
                                  navigate('/case/home', { state: { returnTo: '/visits' } })
                                  return
                                }
                                await store.loadCases()
                                const loaded = useCaseStore.getState().cases?.find(c => c.id === caseId)
                                if (loaded) {
                                  store.setCurrentCase(loaded)
                                }
                                navigate('/case/home', { state: { returnTo: '/visits' } })
                              } catch (err) {
                                console.error('Error loading case:', err)
                                navigate('/case/home', { state: { returnTo: '/visits' } })
                              }
                            }}
                            className="w-full flex items-center gap-2 mb-3 py-2.5 px-3.5 rounded-[10px] active:scale-[0.98] transition-all"
                            style={{ border: '1.5px solid #46A8F9', backgroundColor: '#46A8F908' }}
                          >
                            <FileText size={15} style={{ color: '#46A8F9' }} />
                            <span className="flex-1 text-left text-[13px] font-semibold" style={{ color: '#46A8F9' }}>
                              {t('visits.viewCase')}: {caseFocusArea || getCaseNameById(caseId) || specialty || 'Case'}
                            </span>
                            <ChevronRight size={16} style={{ color: '#A7D9FC' }} />
                          </button>
                        )}

                        {/* v200.2: Action Buttons — Join (video visits only). MEET PA2: the link opens only
                            through JoinVideoVisit (the recording question first); no link yet = the notice. */}
                        {/* ★ PROD-1011: Join (and the no-link stand-in) only for a confirmed visit. */}
                        {!isExternalVisit && isVirtualVisitType(visit.type) && isJoinableVisitStatus(aptStatus) && (videoJoinTarget(visit) ? (
                          <JoinVideoVisit
                            appointment={videoJoinTarget(visit)}
                            beforeOpen={() => openJoinCheckIn(visit)}
                            className="w-full py-2.5 text-white rounded-lg text-[12px] font-semibold flex items-center justify-center gap-1.5 mb-2"
                            style={{ backgroundColor: ZOOM_BLUE }}
                            icon={<Video size={14} />}
                          />
                        ) : (
                          <button
                            onClick={() => alert(t('errors2.videoLinkSent'))}
                            className="w-full py-2.5 text-white rounded-lg text-[12px] font-semibold flex items-center justify-center gap-1.5 mb-2"
                            style={{ backgroundColor: ZOOM_BLUE }}
                          >
                            <Video size={14} />
                            {t(joinVideoVisitLabelKey(hasText))}
                          </button>
                        ))}

                        {/* v200.2: Reschedule + Cancel side by side */}
                        {caseId && (
                          <div className="flex gap-2">
                            {!isExternalVisit && (
                            <button
                              onClick={async () => {
                                try {
                                  const store = useCaseStore.getState()
                                  const existingCase = store.cases?.find(c => c.id === caseId)
                                  if (existingCase) store.setCurrentCase(existingCase)
                                  navigate('/case/home', { state: { openReschedule: true } })
                                } catch {
                                  navigate('/case/home', { state: { returnTo: '/visits' } })
                                }
                              }}
                              className="flex-1 py-2.5 rounded-lg text-[12px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all"
                              style={{ border: '1.5px solid rgba(70,168,249,0.15)', color: '#46A8F9', backgroundColor: 'rgba(70,168,249,0.03)' }}
                            >
                              <RefreshCw size={13} />
                              {t('deliver.reschedule', 'Reschedule')}
                            </button>
                            )}
                            <button
                              onClick={() => setShowCancelConfirm(showCancelConfirm === visit.id ? null : visit.id)}
                              className="flex-1 py-2.5 rounded-lg text-[12px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all"
                              style={{ border: '1.5px solid #fecaca', color: '#dc2626', backgroundColor: '#fef2f2' }}
                            >
                              <X size={13} />
                              {isExternalVisit ? t('common.remove') : t('common.cancel', 'Cancel')}
                            </button>
                          </div>
                        )}

                        {/* v200.2: Cancel confirmation panel */}
                        {showCancelConfirm === visit.id && caseId && (
                          <div className="mt-2 rounded-xl p-4" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                            <div className="flex items-center gap-2 mb-1.5">
                              <AlertTriangle size={16} className="text-red-600 flex-shrink-0" />
                              <p className="text-[14px] font-bold text-red-900">{isExternalVisit ? t('visits.removeOutsideTitle', 'Remove from my visits?') : t('visits.cancelThisAppointment', 'Cancel this appointment?')}</p>
                            </div>
                            <p className="text-[12px] text-red-700 mb-3 leading-relaxed">
                              {isExternalVisit
                                ? t('visits.removeOutsideBody', "This only removes it from Orella. To cancel the visit, contact your doctor's office.")
                                : t('visits.cancelAppointmentBody', 'You can choose a different doctor for this case, or withdraw the case entirely.')}
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setShowCancelConfirm(null)}
                                className="flex-1 py-2.5 rounded-lg text-[12px] font-semibold border border-gray-200 bg-white text-gray-700 active:scale-[0.98] transition-all"
                              >
                                {t('diagnose.keepAppointment')}
                              </button>
                              {isExternalVisit ? (
                              <button
                                disabled={removingId === visit.id}
                                onClick={async () => {
                                  if (removingRef.current) return
                                  removingRef.current = true
                                  setRemovingId(visit.id)
                                  // ★ PROD-1160 (review L2): the outside visit is the patient's own entry. Cancel
                                  //   removes it the way AppointmentEntry does (DELETE /appointments/<id>, which
                                  //   API PROD-1140 allows for the patient's manual_external row only); it never
                                  //   opens the case withdraw / reassign flow of an Orella visit.
                                  try {
                                    await appointmentsAPI.delete(visit.id)
                                    setShowCancelConfirm(null)
                                    setAppointments(prev => prev.filter(a => a.id !== visit.id))
                                    try { await useCaseStore.getState().loadCases?.() } catch { /* the list above is already current */ }
                                  } catch (err) {
                                    console.error('[Visits] Remove outside appointment failed:', err?.status ?? '')
                                    alert(t('errors2.couldNotRemoveAppt'))
                                  } finally {
                                    removingRef.current = false
                                    setRemovingId(null)
                                  }
                                }}
                                className="flex-1 py-2.5 rounded-lg text-[12px] font-bold text-white active:scale-[0.98] transition-all disabled:opacity-60"
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                {t('common.remove')}
                              </button>
                              ) : (
                              <button
                                onClick={async () => {
                                  // Navigate to case home where the cancel/reassign flow lives
                                  try {
                                    const store = useCaseStore.getState()
                                    const existingCase = store.cases?.find(c => c.id === caseId)
                                    if (existingCase) store.setCurrentCase(existingCase)
                                    navigate('/case/diagnose', { state: { openWithdraw: true, returnTo: '/visits' } })
                                  } catch {
                                    navigate('/case/home', { state: { returnTo: '/visits' } })
                                  }
                                }}
                                className="flex-1 py-2.5 rounded-lg text-[12px] font-bold text-white active:scale-[0.98] transition-all"
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                {t('visits.cancelAndReassign')}
                              </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* v227.9.0: Open Pass moved up to the collapsed card (see
                            above) so it no longer requires expanding the visit. */}

                        {/* v177.2.0: Check-in button — confirmed appointments within 24 hours, not yet checked in */}
                        {checkInEligible && !isCheckedIn && (
                          <button
                            onClick={async () => {
                              try {
                                const store = useCaseStore.getState()
                                let existingCase = store.cases?.find(c => c.id === caseId)
                                if (existingCase) {
                                  store.setCurrentCase(existingCase)
                                } else {
                                  console.log('[Visits] Case not in store, fetching:', caseId)
                                  await store.loadCases()
                                  existingCase = useCaseStore.getState().cases?.find(c => c.id === caseId)
                                  if (existingCase) store.setCurrentCase(existingCase)
                                }
                                setCheckInVisitId(visit.id)
                              } catch (err) {
                                console.error('[Visits] Check-in navigation error:', err)
                              }
                            }}
                            className="w-full mt-2 py-3 rounded-lg text-[13px] font-bold flex items-center justify-center gap-2 text-white breathe-green"
                            style={{ backgroundColor: '#46A8F9' }}
                          >
                            <Check size={16} strokeWidth={2.5} />
                            {isVirtualVisitType(visit.type) ? t('videoSignOn.checkInButton') : t('checkIn.checkInForVisit', 'Check in for your visit')}
                          </button>
                        )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            )}

            {/* Past Visits Section */}
            {showPast && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider mb-3 text-gray-400">{t('visits.completed')}</p>
              {pastVisits.length === 0 ? (
                <p className="text-gray-500 text-sm">{t('visits.noPriorVisits')}</p>
              ) : (
                <div className="space-y-3">
                  {pastVisits.map((visit) => {
                    const date = formatDate(visit.date)
                    const practiceName = visit.practice_name || visit.practiceName || ''
                    const specialty = visit.specialty || ''
                    const focusArea = visit.focus_area || visit.focusArea || ''
                    const caseId = visit.case_id || visit.caseId || ''
                    
                    // v200.2: Three-field header data for past visits
                    const pastLinkedCase = getCaseById(caseId)
                    const doctorName = resolveDoctorName(visit, pastLinkedCase)
                    const pastDoctorSpecialty = visit.doctor_specialty || ''
                    const pastPatientCategory = pastLinkedCase?.specialtyName || pastLinkedCase?.specialty?.name || pastLinkedCase?.specialty || ''
                    const pastCaseFocusArea = focusArea || pastLinkedCase?.focusAreaName || pastLinkedCase?.focusArea?.name || pastLinkedCase?.focus_area || ''
                    const pastCaseNumber = pastLinkedCase?.shareCode || pastLinkedCase?.share_code || ''
                    
                    // Use category color — not forced gray
                    const aptCategory = normalizeCategory(visit.appointment_category || visit.appointmentCategory)
                    const cc = getCardColor('confirmed', aptCategory)
                    
                    const handleViewCase = async () => {
                      if (!caseId) return
                      try {
                        // Use the store's loadCases to ensure case is loaded, then navigate
                        const store = useCaseStore.getState()
                        const existingCase = store.cases?.find(c => c.id === caseId)
                        if (existingCase) {
                          store.setCurrentCase(existingCase)
                          navigate('/case/home', { state: { returnTo: '/visits' } })
                          return
                        }
                        // Fallback: load from API
                        await store.loadCases()
                        const loaded = useCaseStore.getState().cases?.find(c => c.id === caseId)
                        if (loaded) {
                          store.setCurrentCase(loaded)
                        }
                        navigate('/case/home', { state: { returnTo: '/visits' } })
                      } catch (err) {
                        console.error('Error loading case:', err)
                        navigate('/case/home', { state: { returnTo: '/visits' } })
                      }
                    }
                    
                    return (
                      <button 
                        key={visit.id} 
                        onClick={handleViewCase}
                        className="w-full text-left bg-white rounded-[14px] overflow-hidden active:scale-[0.99] transition-transform"
                        style={{ border: `1.5px solid ${cc.border}` }}
                      >
                        <div className="h-[3px]" style={{ backgroundColor: cc.primary }} />
                        <div className="p-3.5 flex gap-3.5 items-center">
                          {/* Date square — navy, matching upcoming */}
                          <div className="w-14 h-14 rounded-[12px] flex flex-col items-center justify-center flex-shrink-0" style={{ backgroundColor: '#15354F' }}>
                            <span className="text-[10px] font-bold uppercase" style={{ color: 'rgba(255,255,255,0.6)' }}>{date.month}</span>
                            <span className="text-[22px] font-extrabold text-white leading-none">{date.day}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            {/* v200.2: Three-field header on past visits */}
                            {(pastDoctorSpecialty || pastPatientCategory || pastCaseFocusArea) && (
                              <p className="text-[11px] font-semibold mb-0.5 flex items-center gap-0 flex-wrap">
                                {pastDoctorSpecialty && <span style={{ color: '#46A8F9' }}>{formatProviderSpecialty(pastDoctorSpecialty, t)}</span>}
                                {pastDoctorSpecialty && pastPatientCategory && <span className="text-gray-300 mx-1.5">/</span>}
                                {pastPatientCategory && <span className="text-gray-500">{pastPatientCategory}</span>}
                                {(pastDoctorSpecialty || pastPatientCategory) && pastCaseFocusArea && <span className="text-gray-300 mx-1.5">/</span>}
                                {pastCaseFocusArea && <span className="text-gray-400">{pastCaseFocusArea}</span>}
                              </p>
                            )}
                            <h3 className="text-[14px] font-bold" style={{ color: '#15354F' }}>{doctorName}</h3>
                            {pastCaseNumber && (
                              <p className="text-[10.5px] font-medium text-gray-400 mt-0.5">{t('caseHome.case', 'Case')} {pastCaseNumber}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1.5">
                              <div className="flex items-center gap-1">
                                <CheckCircle size={12} className="text-green-500" />
                                <span className="text-[11px] font-semibold text-green-600">{t('visits.completed')}</span>
                              </div>
                            </div>
                          </div>
                          <ChevronRight size={18} style={{ color: '#46A8F9' }} className="flex-shrink-0" />
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
            )}

            {/* PROD-1137: Cancelled visits. A visit the physician (or his MA) cancelled says so; the
                patient chooses a new time on the case (Case Home), which the card opens. No check-in,
                no join, no reschedule here. */}
            {showCancelled && cancelledVisits.length > 0 && (
            <div className="mt-6">
              <p className="text-[11px] font-bold uppercase tracking-wider mb-3 text-gray-400">{t('visits.filterCancelled')}</p>
              <div className="space-y-3">
                {cancelledVisits.map((visit) => {
                  const date = formatDate(visit.date)
                  const caseId = visit.case_id || visit.caseId || ''
                  const linkedCase = getCaseById(caseId)
                  const doctorName = resolveDoctorName(visit, linkedCase)
                  const byPhysician = rowCancelledByPhysician(visit)
                  const openCase = async () => {
                    if (!caseId) return
                    try {
                      const store = useCaseStore.getState()
                      let found = store.cases?.find(c => c.id === caseId)
                      if (!found) { await store.loadCases(); found = useCaseStore.getState().cases?.find(c => c.id === caseId) }
                      if (found) store.setCurrentCase(found)
                    } catch (err) {
                      console.error('Error loading case:', err?.name)
                    }
                    navigate('/case/home', { state: { returnTo: '/visits' } })
                  }
                  return (
                    <button
                      key={visit.id}
                      data-visit-cancelled={byPhysician ? 'physician' : 'other'}
                      onClick={openCase}
                      className="w-full text-left bg-white rounded-[14px] overflow-hidden active:scale-[0.99] transition-transform"
                      style={{ border: '1.5px solid #E2E8F0' }}
                    >
                      <div className="p-3.5 flex gap-3.5 items-center">
                        <div className="w-14 h-14 rounded-[12px] flex flex-col items-center justify-center flex-shrink-0" style={{ backgroundColor: '#94A3B8' }}>
                          <span className="text-[10px] font-bold uppercase" style={{ color: 'rgba(255,255,255,0.7)' }}>{date.month}</span>
                          <span className="text-[22px] font-extrabold text-white leading-none">{date.day}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[14px] font-bold" style={{ color: '#15354F' }}>{doctorName}</h3>
                          {visit.time && (
                            <p className="text-[11px] text-gray-400 mt-0.5 line-through">{formatTimeDisplay(visit.time)}</p>
                          )}
                          {byPhysician && (
                            <div className="flex items-center gap-1 mt-1.5">
                              <XCircle size={12} style={{ color: '#EF4444' }} />
                              <span className="text-[11px] font-semibold" style={{ color: '#B91C1C' }}>{t('visits.cancelledByPhysician')}</span>
                            </div>
                          )}
                        </div>
                        <ChevronRight size={18} style={{ color: '#46A8F9' }} className="flex-shrink-0" />
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
            )}
          </>
        )}
      </div>

      {/* Add Visit Modal — v155: guides user to start a case (appointment entry lives on Diagnose page) */}
      {showAddVisitModal && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddVisitModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold" style={{ color: '#15354F' }}>{t('visits.addVisit')}</h3>
                <button onClick={() => setShowAddVisitModal(false)}>
                  <X className="text-gray-400" size={22} />
                </button>
              </div>

              <p className="text-gray-500 text-[13px] leading-relaxed mb-5">
                {t('visits.addVisitGuide')}
              </p>

              <div className="space-y-2.5">
                {/* Start new case — Orella doctor */}
                <button
                  onClick={() => { setShowAddVisitModal(false); navigate('/home') }}
                  className="w-full py-3.5 rounded-xl font-semibold text-[14px] text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                  style={{ backgroundColor: '#15354F', boxShadow: '0 4px 14px rgba(21,53,79,0.2)' }}
                >
                  <Calendar size={16} />
                  {t('visits.startNewCase')}
                </button>

                {/* Tip about external flow */}
                <div className="rounded-xl p-3 flex items-start gap-2.5" style={{ background: '#E9F8FF40' }}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#E9F8FF' }}>
                    <Calendar size={12} className="text-[#46A8F9]" />
                  </div>
                  <p className="text-[11.5px] text-gray-500 leading-relaxed">
                    {t('visits.addVisitTip')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </OverlayPortal>
      )}

      {/* v223.9.3: Wallet-style visit pass popup (the designed card) */}
      <WalletPassModal
        isOpen={!!passData}
        onClose={() => setPassData(null)}
        caseId={passData?.caseId}
        patientName={passData?.patientName}
        providerName={passData?.providerName}
        practiceName={passData?.practiceName}
        addressLine1={passData?.addressLine1}
        addressLine2={passData?.addressLine2}
        dateLabel={passData?.dateLabel}
        timeLabel={passData?.timeLabel}
        token={passData?.token}
      />

      {/* v188.5: Check-in / Open Pass modal — opens CheckInModal directly from visits */}
      {checkInVisitId && (() => {
        const activeVisit = appointments.find(a => a.id === checkInVisitId)
        const activeCaseId = activeVisit?.case_id || activeVisit?.caseId
        const activeCase = activeCaseId ? cases.find(c => c.id === activeCaseId) : null
        return (
          <CheckInModal
            isOpen={true}
            joinIntent={checkInJoin}
            onClose={async () => {
              setCheckInVisitId(null)
              setCheckInJoin(false)
              // Refresh cases to pick up checked_in status
              try { await useCaseStore.getState().loadCases() } catch {}
            }}
            doctor={activeCase?.doctor || activeCase?.sentToDoctorData || { name: activeVisit?.doctor_name || activeVisit?.doctorName }}
            appointment={{
              date: activeVisit?.date,
              time: activeVisit?.time,
              type: activeVisit?.type,
              // MEET PA2: the visit's join target (link, id, provider, stored decision) for the
              // pass and the check-in Join button; absent when the visit has no link yet.
              join: videoJoinTarget(activeVisit),
            }}
          />
        )
      })()}

      {/* Hamburger Menu */}
      <HamburgerMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* v217.1.3: Calendar dropdown rendered via portal so it escapes the
          card's overflow-hidden clipping. Positioned with fixed coords from
          the trigger button's bounding rect captured at open time. */}
      {showCalendarMenu && createPortal(
        (() => {
          const allVisits = [...upcomingVisits, ...pastVisits]
          const visit = allVisits.find(v => v.id === showCalendarMenu.id)
          if (!visit) return null
          const linkedCase = getCaseById(visit.case_id || visit.caseId || '')
          const doctorName = resolveDoctorName(visit, linkedCase)
          // v222.4.8 — defensive: practice_address may be a JSONB dict / JSON-string
          const practiceAddress = formatAddress(visit.practice_address || visit.address || '')
          const practiceCity = visit.practice_city || visit.city || ''
          const practiceState = visit.practice_state || ''
          const practiceZip = visit.practice_zip || ''
          const fullAddress = `${practiceAddress}${practiceCity ? ', ' + practiceCity : ''}${practiceState ? ', ' + practiceState : ''} ${practiceZip}`.trim()
          const rect = showCalendarMenu.rect
          // Position the menu below the trigger, right-aligned to it.
          // Menu width = 192px (w-48). Right edge of menu = right edge of trigger.
          const MENU_WIDTH = 192
          const top = rect.bottom + 8
          let left = rect.right - MENU_WIDTH
          // Keep menu fully on-screen with 8px padding from viewport edges.
          if (left < 8) left = 8
          if (left + MENU_WIDTH > window.innerWidth - 8) left = window.innerWidth - MENU_WIDTH - 8
          return (
            <>
              {/* Backdrop to catch outside-clicks */}
              <div
                className="fixed inset-0"
                style={{ zIndex: 9990 }}
                onClick={(e) => { e.stopPropagation(); setShowCalendarMenu(null) }}
              />
              {/* Menu */}
              <div
                className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1"
                style={{ top, left, width: MENU_WIDTH, zIndex: 9991 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleAddToCalendar(visit, doctorName, fullAddress, 'google')
                    setShowCalendarMenu(null)
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Calendar size={14} className="text-gray-400" />
                  {t('visits.googleCalendar', 'Google Calendar')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleAddToCalendar(visit, doctorName, fullAddress, 'outlook')
                    setShowCalendarMenu(null)
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Calendar size={14} className="text-gray-400" />
                  {t('visits.outlookCalendar', 'Outlook Calendar')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleAddToCalendar(visit, doctorName, fullAddress, 'ics')
                    setShowCalendarMenu(null)
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Download size={14} className="text-gray-400" />
                  {t('visits.downloadIcs', 'Download .ics')}
                </button>
              </div>
            </>
          )
        })(),
        document.body
      )}

    </div>
  )
}
